import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import './UserMenu.css';

/**
 * Generate user initials from name, email, or username
 */
export function getInitials(user) {
  if (!user) return 'U';

  const name = user.name || user.displayName;
  if (name && typeof name === 'string') {
    const trimmed = name.trim();
    // Handle "Last, First" format (e.g. Azure AD: "Jerasi, Oren")
    if (trimmed.includes(',')) {
      const [lastPart, firstPart] = trimmed.split(',').map(s => s.trim());
      if (lastPart && firstPart) {
        return (firstPart[0] + lastPart[0]).toUpperCase();
      }
    }
    const parts = trimmed.split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    if (parts.length === 1 && parts[0].length > 0) {
      return parts[0].slice(0, 2).toUpperCase();
    }
  }

  const email = user.email || user.username || '';
  if (email) {
    const local = email.split('@')[0].replace(/[^a-zA-Z0-9]/g, '');
    if (local.length >= 2) {
      return local.slice(0, 2).toUpperCase();
    }
    if (local.length === 1) {
      return local[0].toUpperCase();
    }
  }

  return 'U';
}

/**
 * Get human-readable role & segment label
 */
function getRoleLabel(user) {
  if (!user) return 'Guest';
  if (user.role === 'super_admin') return 'Super Admin';
  if (user.role === 'admin') {
    const segment = user.segment || 'Segment';
    const segmentNames = {
      'OPS_SW': 'OPS-SW',
      'OPS-SW': 'OPS-SW',
      'GROW_PLM': 'Grow@PLM',
      'Grow@PLM': 'Grow@PLM',
      'LCS': 'LCS',
      'ME': 'ME',
      'PES': 'PES',
      'SIM': 'SIM'
    };
    return `${segmentNames[segment] || segment} Admin`;
  }
  return 'Attendee';
}

export const UserMenu = () => {
  const { user, logout, isSuperAdmin, isAdmin } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  const initials = getInitials(user);
  const roleLabel = getRoleLabel(user);
  const displayName = user?.name || user?.displayName || user?.username || 'Siemens User';
  const email = user?.email || (user?.username?.includes('@') ? user.username : '');

  // Close menu on click outside or escape key
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handleLogout = () => {
    setIsOpen(false);
    logout();
  };

  const navigateTo = (path) => {
    setIsOpen(false);
    navigate(path);
  };

  if (!user) return null;

  return (
    <div className="user-menu-container" ref={menuRef}>
      <button
        type="button"
        className="user-avatar-btn"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="true"
        title={`${displayName} (${roleLabel})`}
      >
        <span className="user-avatar-initials">{initials}</span>
      </button>

      {isOpen && (
        <div className="user-dropdown-menu" role="menu">
          <div className="user-dropdown-header">
            <div className="user-dropdown-avatar">
              <span>{initials}</span>
            </div>
            <div className="user-dropdown-info">
              <div className="user-name" title={displayName}>{displayName}</div>
              {email && <div className="user-email" title={email}>{email}</div>}
              <div className="user-role-badge">
                <span className={`role-tag role-${user.role || 'viewer'}`}>{roleLabel}</span>
              </div>
            </div>
          </div>

          <div className="user-dropdown-divider" />

          <div className="user-dropdown-links">
            <button
              type="button"
              className="user-dropdown-item"
              onClick={() => navigateTo('/')}
              role="menuitem"
            >
              📅 Learning Day Agenda
            </button>

            {(user?.role === 'admin' || user?.role === 'super_admin') && (
              <button
                type="button"
                className="user-dropdown-item"
                onClick={() => navigateTo('/admin')}
                role="menuitem"
              >
                ⚙️ Admin Dashboard
              </button>
            )}

            {user?.role === 'super_admin' && (
              <>
                <button
                  type="button"
                  className="user-dropdown-item"
                  onClick={() => navigateTo('/super-admin')}
                  role="menuitem"
                >
                  🛡️ Super Admin Dashboard
                </button>
                <button
                  type="button"
                  className="user-dropdown-item"
                  onClick={() => navigateTo('/health')}
                  role="menuitem"
                >
                  📊 Health & Analytics
                </button>
              </>
            )}
          </div>

          <div className="user-dropdown-divider" />

          <div className="user-dropdown-footer">
            <button
              type="button"
              className="user-dropdown-item logout-item"
              onClick={handleLogout}
              role="menuitem"
            >
              🚪 Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
